import { wxMpBindForLoginUser } from 'api/qywx';
import { changeApp, toLogout, wxUnbind } from 'api/user';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Toast } from 'react-vant';
import { CSCEC3B_APP_ID, isCscec3bTenant } from './cons';
import { isMiniprogram } from './helper';
import { isWx } from './qywx';
import { getHref } from './urls';
import { isArray } from './utils';

export function amI(user: UserInfo, key: ApplicationKey): boolean {
  if (!isArray(user?.applications)) return false;
  const myApp = user?.applications ?? [];
  return myApp[0]?.key === key;
}
export const USER_TYPE = {
  PURCHASER: 1, // 采购商
  SUPPLIER: 2, // 供应商
  SUPPLIER_PARTNER: 4, //供应商子帐号
  PURCHASER_AGENCY: 8, // 采购商代理机构
  SUPPLY_CHAIN: 16, // 赊销系统账号
  OPERATIONS_MANAGEMENT: 64, // (运营管理系统)平台管理员
  SUPPLIER_AUDIT: 128, // 供应商审批审批人
  VIRTUAL_USER: 1024, // 虚拟用户
  PERSONAL: 32, // 个人用户"
};
export function isCurrentUserType(user: UserInfo, key: number): boolean {
  return user?.currentUserType === Number(key);
}

export function canI(user: UserInfo, key: ApplicationKey): boolean {
  const myApp = user?.applications ?? [];
  return myApp.map((a) => a.key).includes(key);
}

/**
 * 必须要 href，不能 router.push，因为需要刷新 userInfo
 */
export function goToIndex(user: UserInfo, isMobile: boolean, force?: ApplicationKey) {
  const goToSupplier = force ? force === 'supplier' : amI(user, 'supplier');
  if (isMobile && goToSupplier) {
    // 供应商去 我的
    window.location.href = getHref('/mine');
  } else {
    // 采购商&运营去 首页
    window.location.href = getHref('/');
  }
}

// h5 退出登录
export async function logout() {
  if (isWx(window.navigator.userAgent) && isCscec3bTenant) {
    // setItem 在 webapp 里，AKA：微信绑定在 webapp 里，解绑在 mallfe 里
    sessionStorage.removeItem('wxToken');
    await wxUnbind({
      appId: CSCEC3B_APP_ID,
    });
  }

  window.location.href = `/api/basic/v2/sso/logout?return_url=${encodeURIComponent(
    getHref('/login'),
  )}`;
}

export function iAm(user: UserInfo): ApplicationKey {
  return user?.applications?.[0].key ?? null;
}

export function switchApp(user: UserInfo, key: ApplicationKey, callback?: () => void) {
  const myApp = user?.applications ?? [];
  if (amI(user, key)) {
    callback ? callback() : gotoMine();
  } else {
    const app = myApp.find((a) => a.key === key);
    if (app) {
      const { sysNo } = myApp.find((a) => a.key === key);
      changeApp(sysNo).then((res) => {
        if (res.code === 200) {
          callback ? callback() : gotoMine();
        } else {
          Toast.fail(res.message);
        }
      });
    } else {
      const keyName = key === 'supplier' ? '供应商' : key === 'purchaser' ? '采购商' : '其他';
      Toast.fail(`当前用户没有${keyName}身份，无法访问${keyName}相关页面`);
    }
  }
}

function gotoMine() {
  // 不用 router,因为要更新 userInfo
  window.location.href = getHref('/mine');
}

const IDASS_BIND_KEY = 'need_to_bind';
const TRY_OPENID_KEY = 'try_to_openid_login';

/**
 * 使用 iDass 在微信里登录，需要在成功登录之后绑定
 * only at client-side
 */
export function rememberToBind(key: ApplicationKey) {
  if (!isWx(window.navigator.userAgent)) {
    return;
  }

  // 只有采购商采用 iDass 登录
  if (key !== 'purchaser') {
    return;
  }

  // boolean enough
  sessionStorage.setItem(IDASS_BIND_KEY, IDASS_BIND_KEY);
}

/**
 * 想起来了就绑定一下
 */
// export function useRecallToBind(userInfo) {
//   const router = useRouter();

//   useEffect(() => {
//     if (!isWx(window.navigator.userAgent)) {
//       return;
//     }
//     isMiniprogram().then(
//       () => {
//         return;
//       },
//       () => {
//         if (!userInfo) {
//           return;
//         }

//         const { code } = router.query;
//         // 已登录，且使用了 iDass 登录，则会尝试去用 wxMpBind 绑定 openId
//         if (sessionStorage.getItem(IDASS_BIND_KEY)) {
//           mpBind2(code as string);
//         }
//       },
//     );

//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);
// }

/**
 * 只尝试一次，避免进入无限循环
 */
export function loginByOpenId(returnUrl: string, fallback: VoidFunction) {
  if (sessionStorage.getItem(TRY_OPENID_KEY)) {
    sessionStorage.removeItem(TRY_OPENID_KEY);
    fallback();
  } else {
    sessionStorage.setItem(TRY_OPENID_KEY, TRY_OPENID_KEY);
    mpBind(returnUrl);
  }
}

/**
 * 如果已登录，且使用了 iDass 登录，则会尝试去用 wxMpBind 绑定 openId
 * 如果未登录，则 wxMpBind 会尝试用 openId 去登录
 * 绑定成不成功都不再绑定，大不了下次登陆再试下
 */
function mpBind(returnUrl = null) {
  const bindUrl = '/api/ifs/v1/wx/wxMpBind';
  const originUrl = window.location.origin;
  const url = encodeURIComponent(window.location.pathname + window.location.search);
  // 不能用 replace，如果接口报错，用户至少可以 back
  window.location.href = `${origin}${bindUrl}?returnUrl=${
    returnUrl || url
  }&appId=${CSCEC3B_APP_ID}&originUrl=${originUrl}`;
}

/**
 * 新加了一个登录后绑定的接口
 * @param code {string} 有 code 说明从微信 oauth2 回来了
 */
function mpBind2(code?: string) {
  if (code) {
    // 绑定成不成功都不再绑定，大不了下次登陆再试下
    sessionStorage.removeItem(IDASS_BIND_KEY);
    wxMpBindForLoginUser(CSCEC3B_APP_ID, code);
  } else {
    // wechat oauth2 and back again
    const redirectUrl = encodeURIComponent(window.location.href);
    window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${CSCEC3B_APP_ID}&redirect_uri=${redirectUrl}&response_type=code&scope=snsapi_base&state=#wechat_redirect`;
  }
}

/**
 * 判断当前用户是否有权限
 * @param {string[]} permissions
 * @param {string} key
 * @returns
 */
export function currentUserHasAuthority(permissions: string[], key: string) {
  if (permissions && permissions.length) {
    return permissions.includes(key);
  }
  return false;
}

// 微信退出登录
export async function wxLogout() {
  const innerLogoutRes = await toLogout();
  // const wxLogoutRes = await unBindAuthWithWxCode();
  if (innerLogoutRes) {
    sessionStorage.clear();
    afterLogout();
  }
}

const logoutCleanUp = () => {
  // 清空浏览器定位、用户选择定位结果
  localStorage.removeItem('TC00000030_locInfo');
  // 清空IP定位或者客户端定位结果
  localStorage.removeItem('last_location');
};

/**
 * 退出登录后，返回首页
 */
const afterLogout = () => {
  if (typeof window !== 'undefined' && (window as any).__wxjs_environment === 'miniprogram') {
    const { wx } = window as any; // 改为使用类型断言访问 wx 属性
    if (wx) {
      wx.miniProgram.navigateTo({ url: '/pages/personalLogout/index' });
    } else {
      throw new Error('jssdk 未准备好');
    }
  } else {
    console.error('不在小程序环境中');
    throw new Error('不在小程序环境中');
  }
};
// 用户退出登录
export const userLogout = () => {
  logoutCleanUp();
  isMiniprogram().then(
    () => wxLogout(),
    // TODO 20231124 微信小程序登录
    () => logout(),
  );
};
